import {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  useEffect,
  type MouseEvent as ReactMouseEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PanelLeftClose,
  PanelLeft,
  Plus,
  Sparkles,
  StopCircle,
  Users,
  Wallet,
  CalendarDays,
  ShieldCheck,
  FileUp,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from './ChatMessage';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { ConversationList } from './ConversationList';
import {
  fetchConversations,
  fetchChatComposerCatalog,
  createConversation,
  fetchConversationWithMessages,
  updateConversationTitle,
  archiveConversation,
  type ApiChatMessage,
} from '@/api/chat';
import { ChatFloatingBar } from './ChatFloatingBar';
import { ensurePlainMathCopyHandler } from './plain-math-copy';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useToast } from '@/hooks/use-toast';
import { FEATURES } from '@/config/features';
import { cn } from '@/lib/utils';
import type { Conversation, ChatMessage as ChatMessageType } from '../types';

function mapApiConversationToList(c: {
  id: number;
  title: string | null;
  updated_at: string;
  _count: { chat_message: number };
}): Conversation {
  return {
    id: c.id,
    title: c.title ?? 'New conversation',
    lastMessage: '',
    timestamp: new Date(c.updated_at),
    messageCount: c._count.chat_message,
  };
}

function mapApiMessageToMessage(m: ApiChatMessage): ChatMessageType {
  return {
    id: m.id,
    role: m.role as 'user' | 'assistant' | 'system',
    content: m.content ?? '',
    timestamp: new Date(m.created_at),
    responseTime: m.response_time ?? null,
    errorMessage: m.error_message ?? null,
    tokens: m.tokens,
    attachments: m.attachments,
    functionResults: m.chat_function_result?.map((fr) => ({
      resultData: fr.result_data,
      functionCalls: fr.function_calls,
      visualizationType: fr.visualization_type,
      functionName: fr.function_name,
      executionTime: fr.execution_time,
    })),
  };
}

const PROMPT_CATEGORIES: {
  label: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  prompts: string[];
}[] = [
  {
    label: 'Workforce',
    description: 'Headcount, departments, and people',
    icon: Users,
    accent: 'bg-sky-500/10 text-sky-700',
    prompts: [
      'How many employees do we have?',
      'List active employees grouped by department',
      'Who joined in the last 30 days?',
      'Show gender distribution by department',
      'Find employees named John',
      'What is our average tenure?',
    ],
  },
  {
    label: 'Payroll',
    description: 'Costs, payslips, and statutory totals',
    icon: Wallet,
    accent: 'bg-emerald-500/10 text-emerald-700',
    prompts: [
      "Summarise last month's payroll",
      'Break down payroll cost by department',
      'Show statutory contributions for this month',
      'What is the salary distribution?',
      'Show Jean Dupont’s pay history for 6 months',
      'How many employees have active loans?',
    ],
  },
  {
    label: 'Leave & attendance',
    description: 'Balances, overtime, and absences',
    icon: CalendarDays,
    accent: 'bg-amber-500/10 text-amber-700',
    prompts: [
      'Who is on leave today?',
      'Show pending leave requests',
      'Summarise overtime this month',
      'Which employees have low annual leave balances?',
      'Show lateness summary for this month',
      'Attendance summary for yesterday',
    ],
  },
  {
    label: 'Compliance',
    description: 'Warnings, qualifications, and holidays',
    icon: ShieldCheck,
    accent: 'bg-violet-500/10 text-violet-700',
    prompts: [
      'List public holidays this year',
      'Show recent disciplinary warnings',
      'Break down employee qualifications',
      'Upcoming birthdays and work anniversaries',
    ],
  },
];

const USAGE_TIPS = [
  'Ask in English or French',
  'Type @ to pick a tool, or / for a tool set',
  'Mention names, departments, or months for sharper results',
  ...(FEATURES.chatAttachments ? ['Drop a PDF or screenshot onto the chat to attach'] : []),
];

const DEFAULT_SIDEBAR_WIDTH = 240;
const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 420;

export function ChatView({
  initialConversationId = null,
}: {
  initialConversationId?: number | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showList, setShowList] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [activeConvId, setActiveConvId] = useState<number | null>(initialConversationId);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    Array<{ id: string; fileName: string; mimeType?: string }>
  >([]);
  const [inputValue, setInputValue] = useState('');
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const fileDragDepthRef = useRef(0);

  const hasFileDrag = useCallback((e: ReactDragEvent) => {
    return Array.from(e.dataTransfer.types).includes('Files');
  }, []);

  const handleChatDragEnter = useCallback(
    (e: ReactDragEvent) => {
      if (!FEATURES.chatAttachments || !hasFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      fileDragDepthRef.current += 1;
      setIsFileDragOver(true);
    },
    [hasFileDrag]
  );

  const handleChatDragLeave = useCallback(
    (e: ReactDragEvent) => {
      if (!FEATURES.chatAttachments || !hasFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (fileDragDepthRef.current === 0) {
        setIsFileDragOver(false);
      }
    },
    [hasFileDrag]
  );

  const handleChatDragOver = useCallback(
    (e: ReactDragEvent) => {
      if (!FEATURES.chatAttachments || !hasFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    },
    [hasFileDrag]
  );

  const handleChatDrop = useCallback((e: ReactDragEvent) => {
    if (!FEATURES.chatAttachments) return;
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current = 0;
    setIsFileDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      void chatInputRef.current?.attachFiles(files);
    }
  }, []);

  useEffect(() => {
    ensurePlainMathCopyHandler();
  }, []);

  useEffect(() => {
    if (initialConversationId != null) {
      setActiveConvId(initialConversationId);
      setShowList(true);
    }
  }, [initialConversationId]);

  const {
    isStreaming,
    streamedContent,
    streamedToolExecutions,
    streamedTotalTokens,
    streamedTotalCost,
    error: streamError,
    streamMessage,
    abort,
    reset,
  } = useStreamingChat();

  const { data: conversationsData = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => fetchConversations('chat'),
  });

  const { data: composerCatalog } = useQuery({
    queryKey: ['chat', 'tools'],
    queryFn: fetchChatComposerCatalog,
    staleTime: 5 * 60 * 1000,
  });

  const conversations: Conversation[] = conversationsData.map(mapApiConversationToList);

  const { data: conversationData, isLoading: messagesLoading } = useQuery({
    queryKey: ['chat', 'conversations', activeConvId],
    queryFn: () => fetchConversationWithMessages(activeConvId!),
    enabled: activeConvId != null,
  });

  const messages: ChatMessageType[] = useMemo(
    () => conversationData?.chat_message.map(mapApiMessageToMessage) ?? [],
    [conversationData]
  );

  const createConvMutation = useMutation({
    mutationFn: async ({
      content,
      attachmentIds,
      attachments,
    }: {
      content: string;
      attachmentIds?: string[];
      attachments?: Array<{ id: string; fileName: string; mimeType?: string }>;
    }) => {
      const title = content.trim().slice(0, 255);
      const conv = await createConversation({ title: title || undefined });
      return { conv, content, attachmentIds, attachments };
    },
    onSuccess: async ({ conv, content, attachmentIds, attachments }) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      setActiveConvId(conv.id);
      setPendingUserMessage(content);
      setPendingAttachments(attachments ?? []);

      try {
        await streamMessage(conv.id, content, { attachmentIds });
      } finally {
        setPendingUserMessage(null);
        setPendingAttachments([]);
      }
    },
  });

  const updateTitleMutation = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: number; title: string }) =>
      updateConversationTitle(conversationId, title),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', conversationId] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (conversationId: number) => archiveConversation(conversationId),
    onSuccess: (_, conversationId) => {
      if (activeConvId === conversationId) {
        abort();
        reset();
        setActiveConvId(null);
        setPendingUserMessage(null);
        setPendingAttachments([]);
      }
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      queryClient.removeQueries({ queryKey: ['chat', 'conversations', conversationId] });
      toast({ title: 'Conversation deleted', description: 'Removed from your chat history.' });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Could not delete conversation',
        description: 'Please try again.',
      });
    },
  });

  useLayoutEffect(() => {
    const viewport =
      scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') ??
      (scrollRef.current?.firstElementChild as HTMLElement | undefined);
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages, streamedContent, streamedToolExecutions]);

  const handleNewConversation = useCallback(() => {
    abort();
    reset();
    setActiveConvId(null);
    setPendingUserMessage(null);
    setPendingAttachments([]);
  }, [abort, reset]);

  const handleSelectConversation = useCallback(
    (id: number) => {
      abort();
      reset();
      setActiveConvId(id);
      setPendingUserMessage(null);
      setPendingAttachments([]);
    },
    [abort, reset]
  );

  const handleDeleteConversation = useCallback(
    (id: number) => {
      archiveMutation.mutate(id);
    },
    [archiveMutation]
  );

  const handleSidebarResizeStart = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      isResizingRef.current = true;
      const startX = event.clientX;
      const startWidth = sidebarWidth;

      const onMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) return;
        const next = Math.min(
          MAX_SIDEBAR_WIDTH,
          Math.max(MIN_SIDEBAR_WIDTH, startWidth + (moveEvent.clientX - startX))
        );
        setSidebarWidth(next);
      };

      const onUp = () => {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [sidebarWidth]
  );

  const handleSendMessage = useCallback(
    async (
      content: string,
      options?: {
        attachmentIds?: string[];
        attachments?: Array<{ id: string; fileName: string; mimeType?: string }>;
      }
    ) => {
      if (activeConvId == null) {
        createConvMutation.mutate({
          content,
          attachmentIds: options?.attachmentIds,
          attachments: options?.attachments,
        });
        return;
      }

      setPendingUserMessage(content);
      setPendingAttachments(options?.attachments ?? []);

      try {
        await streamMessage(activeConvId, content, { attachmentIds: options?.attachmentIds });
      } finally {
        setPendingUserMessage(null);
        setPendingAttachments([]);
      }
    },
    [activeConvId, createConvMutation, streamMessage]
  );

  const handleStop = useCallback(() => {
    abort();
  }, [abort]);

  const isLoading =
    conversationsLoading ||
    (activeConvId != null && messagesLoading) ||
    createConvMutation.isPending ||
    isStreaming;

  const displayMessages = useMemo(() => {
    const result = [...messages];

    if (
      pendingUserMessage &&
      !messages.some((m) => m.content === pendingUserMessage && m.role === 'user')
    ) {
      result.push({
        id: -1,
        role: 'user',
        content: pendingUserMessage,
        timestamp: new Date(),
        responseTime: null,
        errorMessage: null,
        attachments: pendingAttachments,
      });
    }

    // Single assistant bubble: show streaming placeholder only while streaming or until refetch delivers the real message
    const lastMessage = result[result.length - 1];
    const lastIsAssistant = lastMessage?.role === 'assistant';
    const showStreamingBubble =
      (streamedContent || isStreaming) && (!lastIsAssistant || isStreaming);

    if (showStreamingBubble) {
      result.push({
        id: -2,
        role: 'assistant',
        content: streamedContent || '',
        timestamp: new Date(),
        responseTime: null,
        errorMessage: streamError,
        toolCalls: streamedToolExecutions,
        tokens: streamedTotalTokens || undefined,
        streamedCost: streamedTotalCost || undefined,
      });
    }

    return result;
  }, [
    messages,
    pendingUserMessage,
    pendingAttachments,
    streamedContent,
    isStreaming,
    streamError,
    streamedToolExecutions,
    streamedTotalTokens,
    streamedTotalCost,
  ]);

  return (
    <div className="flex h-full">
      {showList && (
        <div className="relative shrink-0" style={{ width: sidebarWidth }}>
          <ConversationList
            conversations={conversations}
            activeId={activeConvId}
            onSelect={handleSelectConversation}
            onNew={handleNewConversation}
            onDelete={handleDeleteConversation}
            isLoading={conversationsLoading}
            createDisabled={false}
            isDeleting={archiveMutation.isPending}
          />
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat history"
            onMouseDown={handleSidebarResizeStart}
            className="absolute inset-y-0 right-0 z-10 w-1 cursor-col-resize bg-transparent transition-colors hover:bg-border active:bg-primary/40"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowList(!showList)}
            >
              {showList ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeft className="h-4 w-4" />
              )}
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">AI Chat</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isStreaming && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={handleStop}>
                <StopCircle className="h-4 w-4" />
                <span className="text-xs">Stop</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground"
              onClick={handleNewConversation}
            >
              <Plus className="h-4 w-4" />
              <span className="text-xs md:text-sm">New conversation</span>
            </Button>
          </div>
        </div>

        <div
          className="relative flex min-h-0 flex-1 flex-col"
          onDragEnter={FEATURES.chatAttachments ? handleChatDragEnter : undefined}
          onDragLeave={FEATURES.chatAttachments ? handleChatDragLeave : undefined}
          onDragOver={FEATURES.chatAttachments ? handleChatDragOver : undefined}
          onDrop={FEATURES.chatAttachments ? handleChatDrop : undefined}
        >
          {FEATURES.chatAttachments && isFileDragOver && (
            <div
              className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-[2px]"
              aria-hidden
            >
              <div
                className={cn(
                  'flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary',
                  'bg-primary/5 px-10 py-8 text-center shadow-sm'
                )}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <FileUp className="h-7 w-7 text-primary" />
                </div>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-foreground">Drop here to attach</p>
                  <p className="text-sm text-muted-foreground">
                    PDF, Word, text, CSV, or images · max 20 MB each
                  </p>
                </div>
              </div>
            </div>
          )}
          {activeConvId == null && !createConvMutation.isPending ? (
            <div className="relative flex min-h-0 flex-1 flex-col">
              <ScrollArea className="min-h-0 flex-1">
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 pb-28 pt-8">
                  <div className="space-y-3 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1.5">
                      <h2 className="text-xl font-semibold tracking-tight">
                        Ask about your payroll data
                      </h2>
                      <p className="mx-auto max-w-lg text-sm text-muted-foreground">
                        Query workforce, payroll, leave, and compliance in plain language. Pick a
                        suggestion below, type @ to choose a tool, or / for a tool set.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                      {USAGE_TIPS.map((tip) => (
                        <span
                          key={tip}
                          className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground"
                        >
                          {tip}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {PROMPT_CATEGORIES.map((category) => {
                      const Icon = category.icon;
                      return (
                        <section key={category.label} className="space-y-3">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${category.accent}`}
                            >
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-medium">{category.label}</h3>
                              <p className="text-xs text-muted-foreground">
                                {category.description}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {category.prompts.map((prompt) => (
                              <button
                                key={prompt}
                                type="button"
                                onClick={() => setInputValue(prompt)}
                                disabled={isLoading}
                                className="rounded-xl border bg-background px-3.5 py-2.5 text-left text-sm shadow-sm transition-colors hover:bg-muted/80 disabled:opacity-50"
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>

              <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center">
                <div className="w-[90%] max-w-3xl">
                  <ChatInput
                    ref={chatInputRef}
                    onSend={handleSendMessage}
                    disabled={isLoading}
                    value={inputValue}
                    onValueChange={setInputValue}
                    catalog={composerCatalog}
                    conversationId={activeConvId}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {conversationData && (
                <div className="absolute left-4 right-4 top-2 z-10 flex justify-center md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-xl">
                  <ChatFloatingBar
                    title={conversationData.title ?? 'New conversation'}
                    onTitleChange={(newTitle) =>
                      updateTitleMutation.mutate({ conversationId: activeConvId!, title: newTitle })
                    }
                    totalTokens={
                      (conversationData.total_tokens ?? 0) + (isStreaming ? streamedTotalTokens : 0)
                    }
                    totalCost={
                      (conversationData.total_cost ?? 0) + (isStreaming ? streamedTotalCost : 0)
                    }
                    totalResponseTimeMs={conversationData.total_response_time_ms ?? 0}
                  />
                </div>
              )}
              <ScrollArea className="min-h-0 flex-1" ref={scrollRef}>
                <div className="space-y-4 p-4 pb-28 pt-16">
                  {displayMessages.length === 0 && !messagesLoading && (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-muted-foreground">
                      <p>No messages yet. Send a message to start.</p>
                    </div>
                  )}
                  {displayMessages.map((msg) => (
                    <ChatMessage
                      key={msg.id}
                      message={msg}
                      isStreaming={msg.id === -2 && isStreaming}
                      toolCalls={msg.id === -2 ? streamedToolExecutions : undefined}
                    />
                  ))}
                </div>
              </ScrollArea>

              <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center">
                <div className="w-[90%] max-w-3xl">
                  <ChatInput
                    ref={chatInputRef}
                    onSend={handleSendMessage}
                    disabled={isStreaming}
                    value={inputValue}
                    onValueChange={setInputValue}
                    catalog={composerCatalog}
                    conversationId={activeConvId}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
